import React, { useState, useEffect } from 'react';
import { User, FileText, Activity, Calendar, TrendingUp, CheckCircle, Clock, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { membersAPI, claimsAPI } from '../../services/apiService';
import {
  getActiveCaseFactors,
  FACTOR_STATUS_FIELD,
  REQUIREMENT_STATUS_FIELD
} from '../../services/pricingService';

const MemberDashboard = ({ member }) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaim, setSelectedClaim] = useState(null);

  useEffect(() => {
    if (!member) {
      setLoading(false);
      return;
    }
    
    if (member.id) {
      fetchClaims();
    } else {
      // If member exists but no id, set loading to false
      setLoading(false);
    }
  }, [member]);

  const fetchClaims = async () => {
    if (!member || !member.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const claimsData = await claimsAPI.getByMember(member.id);
      setClaims(claimsData || []);
      if (claimsData && claimsData.length > 0) {
        setSelectedClaim(claimsData[0]);
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
      toast.error('Failed to load claim information');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'work in progress':
      case 'in progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'not started':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-5 h-5" />;
      case 'work in progress':
      case 'in progress':
        return <Clock className="w-5 h-5" />;
      case 'not started':
        return <XCircle className="w-5 h-5" />;
      default:
        return <Activity className="w-5 h-5" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getActivityUpdates = (claim) => {
    if (!claim) return [];

    const activities = [];
    const pricing = typeof claim.pricing_data === 'string'
      ? (() => { try { return JSON.parse(claim.pricing_data); } catch { return {}; } })()
      : (claim.pricing_data || {});
    const factorValues = pricing.factorValues || {};

    // Prefer case factors with count > 0; fall back to saved requirements list
    const fromFactors = getActiveCaseFactors(factorValues);
    if (fromFactors.length) {
      fromFactors.forEach((factor) => {
        const field = FACTOR_STATUS_FIELD[factor.key];
        const status = field ? (claim[field] || 'Not Started') : 'Not Started';
        activities.push({
          activity: factor.label,
          status,
          updatedAt: claim.updated_at
        });
      });
    } else {
      const requirements = Array.isArray(claim.requirements)
        ? claim.requirements
        : (claim.requirement ? claim.requirement.split(',').map((r) => r.trim()) : []);
      requirements.forEach((req) => {
        const field = REQUIREMENT_STATUS_FIELD[req];
        if (!field) return;
        activities.push({
          activity: req,
          status: claim[field] || 'Not Started',
          updatedAt: claim.updated_at
        });
      });
    }

    return activities.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  };

  if (!member) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500">Member information not available</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const activities = selectedClaim ? getActivityUpdates(selectedClaim) : [];

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <div className="h-20 w-20 bg-primary-100 rounded-full flex items-center justify-center">
            <User className="h-12 w-12 text-primary-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{member?.name || 'Member'}</h1>
            <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">UAN:</span>
                <span className="ml-2 font-semibold text-gray-900">{member?.uan || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-500">Phone:</span>
                <span className="ml-2 font-semibold text-gray-900">{member?.phone_number || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Overview */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-primary-600" />
              Overall Status
            </h2>
            {selectedClaim ? (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Final Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${getStatusColor(selectedClaim.final_status || selectedClaim.status)}`}>
                      {getStatusIcon(selectedClaim.final_status || selectedClaim.status)}
                      <span>{selectedClaim.final_status || selectedClaim.status || 'Not Started'}</span>
                    </span>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Total Balance</span>
                    <span className="font-semibold text-gray-900">₹{parseFloat(selectedClaim.total_balance || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Fees</span>
                    <span className="font-semibold text-gray-900">₹{parseFloat(selectedClaim.fees || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Advance</span>
                    <span className="font-semibold text-gray-900">₹{parseFloat(selectedClaim.advance || 0).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No claim information available</p>
            )}
          </div>

          {/* Claims List */}
          {claims.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-primary-600" />
                Your Claims
              </h2>
              <div className="space-y-2">
                {claims.map((claim) => (
                  <button
                    key={claim.id}
                    onClick={() => setSelectedClaim(claim)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedClaim?.id === claim.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {Array.isArray(claim.requirements) 
                          ? claim.requirements.join(', ') 
                          : (claim.requirement || 'Claim')}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(claim.final_status || claim.status)}`}>
                        {claim.final_status || claim.status || 'Not Started'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Activities and Updates */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-primary-600" />
              Activity Status & Updates
            </h2>
            
            {selectedClaim ? (
              <div className="space-y-4">
                {activities.length > 0 ? (
                  <div className="space-y-3">
                    {activities.map((activity, index) => (
                      <div key={index} className="border-l-4 border-primary-500 pl-4 py-3 bg-gray-50 rounded-r-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{activity.activity}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(activity.status)}`}>
                            {getStatusIcon(activity.status)}
                            <span>{activity.status}</span>
                          </span>
                        </div>
                        <div className="flex items-center text-xs text-gray-500 mt-2">
                          <Calendar className="w-4 h-4 mr-1" />
                          <span>Last updated: {formatDate(activity.updatedAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No activities found for this claim</p>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-4 text-gray-500">Select a claim to view activity status</p>
              </div>
            )}
          </div>

          {/* Recent Updates Timeline */}
          {selectedClaim && (
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-primary-600" />
                Recent Updates
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-2 w-2 bg-primary-500 rounded-full mt-2"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">Claim Created</p>
                    <p className="text-xs text-gray-500">{formatDate(selectedClaim.created_at)}</p>
                  </div>
                </div>
                {selectedClaim.updated_at && selectedClaim.updated_at !== selectedClaim.created_at && (
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-2 w-2 bg-primary-500 rounded-full mt-2"></div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">Last Updated</p>
                      <p className="text-xs text-gray-500">{formatDate(selectedClaim.updated_at)}</p>
                    </div>
                  </div>
                )}
                {selectedClaim.remarks && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-900 mb-1">Remarks</p>
                    <p className="text-sm text-blue-800">{selectedClaim.remarks}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberDashboard;
